import React, { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || "",
  process.env.REACT_APP_SUPABASE_ANON_KEY || ""
);

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    const initializeRecoverySession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      const type = params.get("type");

      if (type === "recovery" && accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) {
          setError(error.message);
          return;
        }
        window.location.hash = "";
        setSessionReady(true);
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setSessionReady(true);
        return;
      }

      setError(
        "Invalid or missing recovery token. Open the reset link from your email."
      );
    };

    initializeRecoverySession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!sessionReady) {
      setError("Password reset link is invalid or expired.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        setError(error.message);
      } else {
        setMessage(
          "Password updated successfully! You can now sign in with your new password."
        );
        setPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <style>{`
        .page {
          --bg: #0b0f14;
          --muted: #93a4b3;
          --text: #e5e7eb;
          --card: rgba(255, 255, 255, 0.06);
          --border: rgba(255, 255, 255, 0.09);
          --accent: #22c55e;
          min-height: 100vh;
          background:
            radial-gradient(40% 50% at 85% 10%, rgba(34, 197, 94, 0.15) 0%, rgba(0,0,0,0) 60%),
            radial-gradient(30% 35% at 10% 20%, rgba(34, 197, 94, 0.1) 0%, rgba(0,0,0,0) 60%),
            linear-gradient(180deg, #0b0f14 0%, #070b10 60%, #05070a 100%);
          color: var(--text);
        }

        .container { max-width: 1100px; margin: 0 auto; padding: 24px; }

        .card {
          max-width: 460px;
          margin: 72px auto;
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
        }

        .title {
          font-size: clamp(24px, 4.5vw, 32px);
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin: 8px 0 18px;
          background: linear-gradient(180deg, #ffffff, #b9c2ca 60%, #7b8794);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          text-align: center;
        }

        .formGroup { margin-bottom: 14px; }
        label { display: block; margin-bottom: 6px; font-size: 14px; color: var(--muted); }
        input {
          width: 100%;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.04);
          color: var(--text);
          font-size: 16px;
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        input:focus { border-color: rgba(134,239,172,.6); box-shadow: 0 0 0 4px rgba(34,197,94,.12); }

        .alert { margin: 12px 0; padding: 10px 12px; border-radius: 10px; font-size: 14px; }
        .alert--error { color: #fecaca; background: rgba(239,68,68,.1); border: 1px solid rgba(239,68,68,.35); }
        .alert--success { color: #bbf7d0; background: rgba(34,197,94,.08); border: 1px solid rgba(34,197,94,.35); }

        .actions { margin-top: 16px; }
        .btn {
          width: 100%;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 12px 18px; border-radius: 999px;
          border: 1px solid var(--border); background: var(--card); color: var(--text);
          text-decoration: none; transition: transform .15s ease, background .2s ease, border-color .2s ease;
          font-weight: 700;
        }
        .btn:hover { transform: translateY(-1px); border-color: rgba(255,255,255,.2); }
        .btnPrimary { background: linear-gradient(180deg, #2fe26f, #16a34a); color: #061007; border: none; }
        .btn[disabled] { opacity: .7; cursor: not-allowed; transform: none; }
      `}</style>

      <div className="container">
        <section className="card">
          <h1 className="title">Reset Your Password</h1>
          <form onSubmit={handleSubmit}>
            <div className="formGroup">
              <label htmlFor="password">New Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="formGroup">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {error && <div className="alert alert--error">{error}</div>}
            {message && <div className="alert alert--success">{message}</div>}

            <div className="actions">
              <button
                type="submit"
                className="btn btnPrimary"
                disabled={loading}
              >
                {loading ? "Updating Password..." : "Update Password"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ResetPassword;
